import React, { useState, useEffect } from "react";
import styled from "styled-components";
import magnifyGlassIcon from "@images/icons/magnifying-glass.svg";
import MapTo from "@utils/mapto";

import { ProcedureSelect } from "./ProcedureSelect";


const Breakpoints = {
  Mobile: "768px",
  Tablet: "1024px",
};

const MediaQueries = {
  Mobile: `@media (max-width: ${Breakpoints.Mobile})`,
  Tablet: `@media (max-width: ${Breakpoints.Tablet})`,
};
// Procedure types
const procedureData = {
  "Abdominal": [
    { id: "result1", name: "Laparoscopic Colorectal Surgery" }
  ],
  "Spine": [
    { id: "result2", name: "Minimally Invasive TLIF (MIS TLIF)" },
    { id: "result3", name: "Unilateral, Single-Level TLIF" }
  ],
  "Women's Health": [
    { id: "result4", name: "Implant-Based Breast Reconstruction: California" },
    { id: "result5", name: "Implant-Based Breast Reconstruction: Illinois" },
    { id: "result6", name: "Abdominally Based Microsurgical Breast" },
    { id: "result7", name: "Microvascular Breast Reconstruction" },
    { id: "result8", name: "Gynecologic Oncology Robotic-Assisted Hysterectomy" },
    { id: "result9", name: "Cesarean Section" }
  ],
  "Orthopedic": [
    { id: "result10", name: "Total Hip Arthroplasty" },
    { id: "result11", name: "Total Join Arthroplasty Study: Medicare & Commercial" },
    { id: "result12", name: "Total Joint Arthroplasty: Illinois" },
    { id: "result13", name: "Total Joint Arthroplasty: New Jersey" },
    { id: "result14", name: "Total Knee Arthroplasty" },
    { id: "result15", name: "Total Shoulder Arthroplasty of Rotator Cuff Repair" },
    { id: "result16", name: "Shoulder Arthroplasty" }
  ]
};

// Styled components
const ProcedureContainer = styled.div`
  width: 100%;
`;

const SelectionContainer = styled.div`
  display: flex;
  gap: 24px;

  ${MediaQueries.Tablet} {
    flex-direction: column;
    gap: 16px;
  }
`;

const SelectGroup = styled.div`
  flex: 1;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: left;
  padding: 64px 0;
  ${MediaQueries.Tablet} {
    padding: 32px 0 48px;
  }
`;

const DefaultMessage = styled.div.attrs({
  className: "inital-message"
})`
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: start;
  width: 100%;
`;

const ContentSection = styled.div.attrs({
  className: "procedure-content-section"
})`
  p {
    color: rgba(0, 0, 0, 0.7);
  }
  ul {
    padding-left: 20px;
    padding-top: 8px;
    padding-bottom: 24px;

    li {
      padding-bottom: 8px;
      &:last-child {
        padding-bottom: 0;
      }
        b {
        font-weight: 500;
        }
    }
      ul {
        padding-bottom: 0;
      }
  }
  ul:has(li.list-none) {

    @media (min-width: 1024px) {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 40px;
      padding-top: 24px;

      li {
        padding-bottom: 0;
        >div {
          top:15px
        }
      }
    }
  }

  #result4 {
    @media (min-width: 1024px) {
      ul:has(li.list-none) {
        li {
          &:nth-child(2) {
            margin-left: 25px;
          }
        }
      }
    }
  }

  #result8 {
    @media (min-width: 1024px) {
      ul:has(li.list-none) {
        grid-template-columns: repeat(3, 1fr);
      }
    }
  }

  #result9 {
    @media (min-width: 1024px) {
      ul:has(li.list-none) {
        grid-template-columns: repeat(3, 1fr);

        > :nth-child(4) {
          grid-column: 1;
        }

        > :nth-child(5) {
          grid-column: 2;
        }
      }
    }
  }

  #result10 {
    @media (min-width: 1024px) {
      ul:has(li.list-none) {
        grid-template-columns: repeat(2, 1fr);
      }
    }
  }

  #result13 {
    @media (min-width: 1024px) {
      ul:has(li.list-none) {
        grid-template-columns: repeat(3, 1fr);
      }
    }
  }

  #result15 {
      @media (min-width: 1024px) {
      ul:has(li.list-none) {
        grid-template-columns: 50% 20% 20%;
      }
  }
`;

const ProcedureType = ({ style, items }) => {
  // State for selected options
  const [selectedProcedure, setSelectedProcedure] = useState("");
  const [selectedStudy, setSelectedStudy] = useState("");
  const [availableStudies, setAvailableStudies] = useState([]);
  const [activeResult, setActiveResult] = useState("");
  const [lastValidResult, setLastValidResult] = useState("");
  const [hasShownResult, setHasShownResult] = useState(false);

  const getContent = (items) => {
    if (items?.length > 0) {
      return items.map((item) => (
        <MapTo items={item} key={item.contentfulid} className="container-items" />
      ));
    }
    return null;
  };

  // Block to handle updating the dropdowns based on query param values
  const slugify = (str) =>
    str
      .toLowerCase()
      .normalize("NFD")                      // Handle accented characters
      .replace(/[\u0300-\u036f]/g, "")      // Remove diacritics
      .replace(/['":,]/g, "")               // Remove quote/colon/comma
      .replace(/\s*-\s*/g, "-")             // Normalize dashes with/without spaces
      .replace(/[^a-z0-9-]+/g, "-")         // Replace non-alphanumeric (except dash) with dashes
      .replace(/-+/g, "-")                  // Collapse multiple dashes
      .replace(/^-+|-+$/g, "");

  const procedureSlugMap = Object.keys(procedureData).reduce((acc, label) => {
    acc[slugify(label)] = label;
    return acc;
  }, {});

  const studySlugMap = Object.values(procedureData).flat().reduce((acc, study) => {
    acc[slugify(study.name)] = study.name;
    return acc;
  }, {});

  useEffect(() => {
    if (!procedureData || Object.keys(procedureData).length === 0) return;

    const searchParams = new URLSearchParams(window.location.search);
    const procedureSlug = searchParams.get("type");
    const studySlug = searchParams.get("study");

    const procedureFromUrl = procedureSlugMap[procedureSlug];
    const studyFromUrl = studySlugMap[studySlug];

    if (procedureFromUrl && procedureData[procedureFromUrl]) {
      setSelectedProcedure(procedureFromUrl);

      if (studyFromUrl && procedureData[procedureFromUrl].some(s => s.name === studyFromUrl)) {
        setTimeout(() => {
          setSelectedStudy(studyFromUrl);
        }, 0);
      }
    }
  }, [procedureData]);

  // Update available studies when procedure type changes
  useEffect(() => {
    if (selectedProcedure && procedureData[selectedProcedure]) {
      setAvailableStudies(procedureData[selectedProcedure]);
      setSelectedStudy(prev => (prev ? prev : "")); // Reset study selection when procedure changes
    } else {
      setAvailableStudies([]);
      setSelectedStudy("");
    }
  }, [selectedProcedure]);

  // Update activeResult when procedure or study selections change
  useEffect(() => {
    if (selectedProcedure && selectedStudy) {
      // Find the matching study in procedureData to get its ID
      const selectedStudyObj = procedureData[selectedProcedure]?.find(
        study => study.name === selectedStudy
      );
      const newResult = selectedStudyObj?.id || "";
      setActiveResult(newResult);
      if (newResult) {
        setLastValidResult(newResult);
        setHasShownResult(true);
      }
    } else if (!selectedProcedure && !selectedStudy) {
      // Only reset active result when both selections are cleared
      setActiveResult("");
    }
  }, [selectedProcedure, selectedStudy]);

  // Display result ID to use (active if available, otherwise last valid)
  const displayResultId = activeResult || lastValidResult;

  // Handle procedure type change
  const handleProcedureChange = (e) => {
    setSelectedProcedure(e.target.value);
  };

  // Handle study type change
  const handleStudyChange = (e) => {
    setSelectedStudy(e.target.value);
  };

  // Create options for procedure types
  const procedureOptions = Object.keys(procedureData).map(procedure => ({
    label: procedure,
    value: procedure
  }));

  // Create options for study types
  const studyOptions = availableStudies.map(study => ({
    label: study.name,
    value: study.name
  }));

  return (
    <ProcedureContainer style={style}>
      <SelectionContainer className="selection-container">
        <SelectGroup className="selection-procedure">
          <ProcedureSelect
            label="Procedure Type"
            name="procedureType"
            value={selectedProcedure}
            onChange={handleProcedureChange}
            options={procedureOptions}
            placeholder="Procedure Type"
            additionalText="(Choose One)"
          />
        </SelectGroup>

        <SelectGroup className="selection-study">
          <ProcedureSelect
            label="Study Type"
            name="studyType"
            value={selectedStudy}
            onChange={handleStudyChange}
            options={studyOptions}
            placeholder="Study"
            additionalText="(Choose One)"
            disabled={!selectedProcedure}
          />
        </SelectGroup>
      </SelectionContainer>

      <ContentWrapper>
        {/* Default message only shown if no result has ever been selected */}
        {!hasShownResult && (!selectedProcedure || !selectedStudy) && (
          <DefaultMessage>
            <img src={magnifyGlassIcon} alt="Select options" className="w-[120px] h-[120px] mx-auto" />
            <p className="text-[14px] lg:text-p text-black/70 lg:max-w-[400px] mx-auto pb-12 lg:pb-16 text-center">Please select a procedure & study to see associated clinical and economic outcomes.</p>
            <p className="text-p_footnote text-black/70 lg:max-w-[520px] lg:ml-0 ml-2 relative text-left"><span className="absolute top-0 -left-2">*</span>The clinical benefit of the decrease in opioid consumption was not demonstrated in pivotal trials LOS, length of stay.</p>
          </DefaultMessage>
        )}

        {/* Render items if provided */}
        {items && (
          <ContentSection className={`w-full ${hasShownResult ? 'block' : 'hidden'}`}>
            {Array.isArray(items) ? (
              items.map((item, index) => {
                return (
                  <div
                    key={index}
                    id={item.contentfulid}
                    className={`container-items ${displayResultId === item.contentfulid ? "block" : "hidden"}`}
                  >
                    {item.entryTitle && <h3>{getContent(item.items)}</h3>}
                  </div>
                );
              })
            ) : (
              <div className={displayResultId === items.contentfulid ? "block" : "hidden"}>
                {items.entryTitle && <h3>{items.entryTitle}</h3>}
              </div>
            )}
          </ContentSection>
        )}

      </ContentWrapper>
    </ProcedureContainer>
  );
};

export default ProcedureType;

